/**
 * Sound utilities for UI feedback
 * Uses Web Audio API to generate simple notification sounds
 */

// Audio context singleton - shared across all sounds
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
}

/**
 * Prime the audio context - call this from a user gesture
 * This satisfies browser autoplay restrictions
 */
export function primeNotificationSound(): void {
  try {
    const ctx = getAudioContext();
    // Resume the context if it's suspended (happens due to autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
  } catch {
    // Audio context not available - fail silently
  }
}

/**
 * Play a simple notification ding
 * A single gentle tone
 */
export function playNotificationSound(): void {
  try {
    const ctx = getAudioContext();

    // Try to resume if not primed (may fail without user gesture)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const now = ctx.currentTime;

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Single ding at A5 (880 Hz)
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, now);

    // Quick attack, gentle decay
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.2, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

    oscillator.start(now);
    oscillator.stop(now + 0.4);
  } catch {
    // Audio not available - fail silently
  }
}
